import {openDialog, Tooltip, useTranslationContext} from 'platform/components';

import {suffixTestId, type TestIdProps} from 'shared/web';

import {PlusGlyph} from '../../../icons/StrokeGlyphs';
import {EditPresetDialog} from '../EditPresetDialog/EditPresetDialog';

type AddPresetTabProps = TestIdProps & {
  /** Opens the create-preset dialog and persists the new preset on submit. */
  onCreate: (name: string, desc: string, color?: string) => Promise<void>;
  isDisabled?: boolean;
};

/**
 * The "+" add-view tab that trails the preset strip (the mock's `.view__add` tab):
 * a borderless ghost tab in fg-tertiary (#7A869A) with a thin-stroke + glyph that
 * darkens to fg-primary on the neutral-20 hover background (mock `.view:hover`). The
 * foundation Material `content/add` icon renders heavier and navy, so the glyph is
 * inlined to match the mock exactly. Clicking it opens the create-preset dialog.
 */
export function AddPresetTab(props: AddPresetTabProps) {
  const t = useTranslationContext();

  return (
    <Tooltip label={t('page.actions.createPreset')} placement="top">
      <button
        type="button"
        disabled={props.isDisabled}
        aria-label={t('page.actions.createPreset')}
        data-testid={suffixTestId('createPresetButton', props)}
        onClick={() =>
          openDialog(
            <EditPresetDialog
              onSubmit={props.onCreate}
              data-testid={suffixTestId('createPreset', props)}
            />,
            {
              id: 'createPreset',
              title: t('general.presets.createPreset'),
              'data-testid': suffixTestId('createPreset', props),
              size: 'small',
              scrollBehavior: 'outside',
            }
          )
        }
        // Styled inline (like the sibling scroll/more buttons): this repo
        // ships a prebuilt Tailwind bundle, so new arbitrary utilities
        // (px-[10px]) and even ml-1 are absent and render nothing. Inline
        // guarantees the ghost-tab look. fg-secondary (#42526E, same as the
        // dropdown variant's create button) instead of the mock's fg-tertiary:
        // the lighter glyph read as a disabled button (T20-134467 OG feedback).
        style={{
          background: 'transparent',
          border: 0,
          color: '#42526E',
          height: 32,
          padding: '0 8px',
          marginLeft: 2,
        }}
        className="flex-shrink-0 self-center inline-flex items-center justify-center rounded-[4px] cursor-pointer hover:bg-[#EBECF0]"
      >
        <PlusGlyph />
      </button>
    </Tooltip>
  );
}
